"use client";

import { useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from "react-icons/fa";
import emailjs from "emailjs-com";
import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";



function Contact() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formState,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setIsSubmitting(false);
        setIsSubmitted(true);
        setFormState({
          name: "",
          email: "",
          phone: "",
          service: "",
          message: "",
        });
      })
      .catch((error) => {
        setIsSubmitting(false);
        alert("Something went wrong. Please try again.");
        console.error("EmailJS error:", error);
      });
  };

  return (
    <>
    <Helmet>
        <title>{t("seo.contact.title")}</title>
        <meta name="description" content={t("seo.contact.description")} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${window.location.origin}/${currentLang}/contact`} />

        <meta property="og:title" content={t("seo.contact.title")} />
        <meta property="og:description" content={t("seo.contact.description")} />
        <meta property="og:url" content={`${window.location.origin}/${currentLang}/contact`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("seo.contact.title")} />
        <meta name="twitter:description" content={t("seo.contact.description")} />
        <meta name="twitter:image" content="path/to/your/image.jpg" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white page-header">
        <Container>
          <div style={{ maxWidth: "800px" }}>
            <h1 className="display-4 fw-bold mb-4">{t("contact.hero.title")}{" "}</h1>
            <p className="fs-4 text-white-80">
            {t("contact.hero.description")}{" "}
            </p>
          </div>
        </Container>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="g-4">
            <Col lg={6}>
              <Card className="shadow h-100">
                <Card.Body className="p-4">
                  <h2 className="mb-4">{t("contact.form.title")}{" "}</h2>

                  {isSubmitted ? (
                    <div className="bg-success bg-opacity-10 border border-success border-opacity-25 text-success p-4 rounded">
                      <h3 className="fs-5 fw-bold mb-2">{t("contact.form.success.title")}{" "}</h3>
                      <p className="mb-0">
                        {t("contact.form.success.message")}{" "}
                      </p>
                    </div>
                  ) : (
                    <Form onSubmit={handleSubmit}>
                      <Row className="g-3 mb-3">
                        <Col md={6}>
                          <Form.Group controlId="name">
                            <Form.Label>{t("contact.form.fullName")}{" "}</Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              value={formState.name}
                              onChange={handleChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="email">
                            <Form.Label>{t("contact.form.email")}{" "}</Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              value={formState.email}
                              onChange={handleChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="g-3 mb-3">
                        <Col md={6}>
                          <Form.Group controlId="phone">
                            <Form.Label>{t("contact.form.phone")}{" "}</Form.Label>
                            <Form.Control
                              type="tel"
                              name="phone"
                              maxLength={15}
                              value={formState.phone}
                              onChange={(e) => {
                                const onlyNums = e.target.value.replace(/\D/g, ""); // keep only digits
                                handleChange({
                                  target: {
                                    name: e.target.name,
                                    value: onlyNums,
                                  },
                                });
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="service">
                            <Form.Label>{t("contact.form.service")}{" "}</Form.Label>
                            <Form.Select
                              name="service"
                              value={formState.service}
                              onChange={handleChange}
                            >
                              <option value=""> {t("contact.form.standard.option")}{" "}</option>
                              <option value="shell-construction">
                                {t("contact.form.shell.option")}{" "}
                              </option>
                              <option value="foundation-work">
                              {t("contact.form.foundation.option")}{" "}
                              </option>
                              <option value="concrete-structures">
                              {t("contact.form.concrete.option")}{" "}
                              </option>
                              <option value="industrial-construction">
                              {t("contact.form.industrial.option")}{" "}
                              </option>
                              <option value="renovation">{t("contact.form.renovation.option")}{" "}</option>
                              <option value="project-management">
                              {t("contact.form.management.option")}{" "}
                              </option>
                              <option value="project-management">
                              {t("contact.form.other.option")}{" "}
                              </option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-4" controlId="message">
                        <Form.Label> {t("contact.form.message")}{" "}</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          name="message"
                          value={formState.message}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>

                      <Button
                        type="submit"
                        variant="tertiary"
                        className="w-100"
                        disabled={isSubmitting}
                      >
                      {isSubmitting ? t("contact.form.sending") : t("contact.form.button")}
                      </Button>
                    </Form>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6}>
              <h2 className="mb-4">{t("contact.info.title")}{" "}</h2>

              <div className="mb-4">
                <div className="d-flex mb-3">
                  <FaMapMarkerAlt className="text-tertiary fs-4 me-3 mt-1" />
                  <div>
                    <h3 className="fs-5 fw-bold mb-2">{t("contact.info.address.title")}{" "}</h3>
                    <p className="text-secondary mb-0">
                      {t("contact.info.address.line1")}{" "}
                    <br />
                    {t("contact.info.address.line2")}{" "}
                      <br />
                      {t("contact.info.address.line3")}{" "}
                    </p>
                  </div>
                </div>

                <div className="d-flex mb-3">
                  <FaPhone className="text-tertiary fs-4 me-3 mt-1" />
                  <div>
                    <h3 className="fs-5 fw-bold mb-2">{t("contact.info.phone.title")}{" "}</h3>
                    <p className="text-secondary mb-0">
                    {t("contact.info.phone.main")}{" "}
                      <br />
                      {t("contact.info.phone.support")}{" "}
                    </p>
                  </div>
                </div>

                <div className="d-flex mb-3">
                  <FaEnvelope className="text-tertiary fs-4 me-3 mt-1" />
                  <div>
                    <h3 className="fs-5 fw-bold mb-2">{t("contact.info.email.title")}{" "}</h3>
                    <p className="text-secondary mb-0">
                    {t("contact.info.email.main")}{" "}                      
                    <br />
                    {t("contact.info.email.support")}{" "}
                    </p>
                  </div>
                </div>

                <div className="d-flex mb-4">
                  <FaClock className="text-tertiary fs-4 me-3 mt-1" />
                  <div>
                    <h3 className="fs-5 fw-bold mb-2">{t("contact.info.hours.title")}{" "}</h3>
                    <p className="text-secondary mb-0">
                      {t("contact.info.hours.weekdays")}{" "}
                      <br />
                      {t("contact.info.hours.saturday")}{" "}
                      <br />
                      {t("contact.info.hours.sunday")}{" "}
                    </p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

export default Contact;
